"use client";

import type React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Flex, Spinner, useToast } from "@chakra-ui/react";
import WhatsAppAgentConfig from "./WhatsAppAgentConfig";
import {
  whatsappAgentService,
  type WhatsAppAgent,
} from "../../whatsapp-services/agentService";
import { useAuth } from "../../contexts/AuthContext";

const WhatsAppAgentRouter: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { credits, creditsLoading } = useAuth(); // Get creditsLoading state
  const [agent, setAgent] = useState<WhatsAppAgent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasMountedRef = useRef(false);
  const hasShownCreditWarning = useRef(false);

  useEffect(() => {
    if (
      !hasMountedRef.current &&
      !creditsLoading &&
      credits !== null &&
      credits < 0.01 &&
      !hasShownCreditWarning.current
    ) {
      hasShownCreditWarning.current = true;
      toast({
        title: "Insufficient Credits",
        description:
          "You need to have credits in your wallet in order to access WhatsApp agents.",
        status: "error",
        duration: 5000,
      });
      navigate("/whatsapp-agents");
    }
    hasMountedRef.current = true;
  }, [credits, creditsLoading, navigate, toast]); // Add creditsLoading to dependencies

  useEffect(() => {
    const fetchAgent = async () => {
      if (!agentId) return;

      try {
        const fetchedAgent = await whatsappAgentService.get(agentId);
        setAgent(fetchedAgent);
      } catch (error) {
        console.error("Error fetching WhatsApp agent:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgent();
  }, [agentId]);

  if (!agentId || isLoading) {
    return (
      <Flex h="100vh" justify="center" align="center" bg="white">
        <Spinner size="xl" color="primary.500" />
      </Flex>
    );
  }

  if (!agent) {
    return null;
  }

  return <WhatsAppAgentConfig agent={agent} />;
};

export default WhatsAppAgentRouter;
